<template>
  <div
    class="MyMapDomaine"
    :class="{ 'MyMapDomaine--expanded': !hideDetails }"
    :id="domaine.domaineCode"
    v-show="isShown"
  >
    <div class="MyMapDomaine__content" @click="toggleDetails">
      <div class="MyMapDomaine__icon">
        <Icon :icon="domaineIcon" :color="iconColor" :size="22" />
      </div>
      <div class="MyMapDomaine__info">
        <span class="MyMapDomaine__lib" :style="labelStyle">{{ formatDomaineLib(libelleDynamique(domaine.domaineLib, domaine.domaineLibEn)) }}</span>
      </div>
      <Icon
        icon="mdi:chevron-down"
        class="MyMapDomaine__chevron"
        size="20"
      />
    </div>
    <div v-if="!hideDetails" class="MyMapDomaine__details">
      <template v-if="selectedSousDomaineId !== null">
        <MyMapSousDomaineIndicateurs
          v-if="selectedSousDomaine"
          :sous-domaine="selectedSousDomaine"
          :domaine-code="domaine.domaineCode"
          :domaine-lib="domaine.domaineLib"
          :infrastructure-layer-name="infrastructureLayerName"
          @back="selectedSousDomaineId = null"
        />
      </template>
      <template v-else>
        <div v-if="sousDomaines.length > 0" class="MyMapDomaine__sousDomaines">
          <MyMapSousDomaine
            v-for="sousDomaine in sousDomaines"
            :key="sousDomaine.sousDomaineId"
            :sous-domaine="sousDomaine"
            @navigate="navigateToSousDomaine"
          />
        </div>
        <p v-else class="MyMapDomaine__noSousDomaines">
          {{ $t('myMap.domaines.noSousDomaines', 'Aucun sous-domaine disponible') }}
        </p>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Domaine } from '@/models/interfaces/Domaine'
import type { SousDomaine } from '@/models/interfaces/SousDomaine'
import { useMyMapStore } from '@/stores/myMapStore'
import { useSousDomaineStore } from '@/stores/sousDomaineStore'
import { useAttributStore } from '@/stores/attributStore'
import Icon from '@/components/ui/Icon.vue'
import MyMapSousDomaine from '@/views/map/components/Domaines/MyMapSousDomaine.vue'
import MyMapSousDomaineIndicateurs from '@/views/map/components/Domaines/MyMapSousDomaineIndicateurs.vue'
import { getDomaineIcon } from '@/data/domaineCompetenceIcons'
import { formatDomaineLib } from '@/services/utils/UtilsService'
import { libelleDynamique } from '@/utils/libelleDynamique'
import { computed, ref } from 'vue'

const props = defineProps<{
  domaine: Domaine
  highlightOrange?: boolean
}>()

const hideDetails = ref(true)
const selectedSousDomaineId = ref<number | null>(null)
const mapStore = useMyMapStore()
const sousDomaineStore = useSousDomaineStore()
const attributStore = useAttributStore()

const isShown = computed(() => !mapStore.activeAtlas.leftPanel.active)

const labelStyle = computed(() => {
  if (!props.highlightOrange) return undefined
  return { color: '#e67e22' }
})

const iconColor = computed(() => (props.highlightOrange ? '#e67e22' : 'main-blue'))

/** Hidden from the left panel: catch-all bucket, not a real thematic sous-domaine. */
const HIDDEN_SOUS_DOMAINE_LIBS = ['autres données non classées', 'bâtiments administratifs']

const sousDomaines = computed(() => {
  return sousDomaineStore
    .getByDomaineId(props.domaine.domaineId)
    .filter((sousDomaine) => !HIDDEN_SOUS_DOMAINE_LIBS.includes(sousDomaine.sousDomaineLib.trim().toLowerCase()))
})

const INFRASTRUCTURE_VIEW_PREFIX = 'view_infrastructures_'

const infrastructureLayerName = computed(() => {
  return `${INFRASTRUCTURE_VIEW_PREFIX}${props.domaine.domaineCode.toLowerCase()}`
})

const domaineIcon = computed(
  () => props.domaine.domaineIcon ?? getDomaineIcon(props.domaine.domaineCode)
)

const selectedSousDomaine = computed<SousDomaine | null>(() => {
  if (selectedSousDomaineId.value === null) return null
  return sousDomaines.value.find((s) => s.sousDomaineId === selectedSousDomaineId.value) ?? null
})

const toggleDetails = async () => {
  hideDetails.value = !hideDetails.value
  if (hideDetails.value) {
    selectedSousDomaineId.value = null
  } else {
    if (sousDomaineStore.sousDomaineList.length === 0) {
      await sousDomaineStore.getAll()
    }
    if (attributStore.indicateurPcdList.length === 0) {
      await attributStore.getIndicateursPcd()
    }
  }
}

function navigateToSousDomaine(sousDomaine: SousDomaine): void {
  selectedSousDomaineId.value = sousDomaine.sousDomaineId
}
</script>

<style lang="scss">
.MyMapDomaine {
  display: flex;
  flex-flow: column nowrap;
  border-radius: var(--dim-radius);
  border: 1px solid var(--neutral-border);
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &__content {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    min-height: var(--panel-row-min-height);
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__info {
    display: flex;
    flex-flow: column nowrap;
    gap: 0.25rem;
    flex: 1;
    min-width: 0;
  }

  &__lib {
    font-size: var(--font-size-h6);
    font-weight: 600;
    color: rgb(var(--v-theme-main-blue));
  }

  &__chevron {
    font-size: 1.25rem;
    color: rgba(0, 0, 0, 0.54);
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }

  &--expanded &__chevron {
    transform: rotate(180deg);
  }

  &__details {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    margin-left: 0;
    margin-right: 0;
    border-top: 1px solid var(--neutral-border);
  }

  &__sousDomaines {
    display: flex;
    flex-flow: column nowrap;
    gap: 0;
    margin-top: 0.5rem;
  }

  &__noSousDomaines {
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.4);
    font-style: italic;
    margin-top: 0.5rem;
  }
}
</style>
